import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import QualificationAnimation from './QualificationAnimation';
import { brand, ctaPrimary, ctaSecondary } from '../../shared-theme/brand';

// Rotating phrases for the hero title
const rotatingPhrases = ['a lead', 'a call', 'a text', 'a chat', 'a client', 'a case'];
const ANIMATION_DURATION = 500; // ms
const DISPLAY_DURATION = 3000; // ms

// Shared text styles for the rotating phrase (5% larger than base text)
const phraseStyles = {
  fontFamily: '"Instrument Serif", serif',
  fontStyle: 'italic' as const,
  color: '#B5683C',
  whiteSpace: 'nowrap' as const,
  fontSize: '1.08em', // 5% larger than surrounding text
};

// Inline waveform animation for the play button
const waveformBars = [
  { height: 0.3, delay: 0 },
  { height: 0.5, delay: 0.1 },
  { height: 0.8, delay: 0.2 },
  { height: 0.4, delay: 0.15 },
  { height: 0.9, delay: 0.25 },
  { height: 0.6, delay: 0.05 },
  { height: 0.7, delay: 0.3 },
  { height: 0.5, delay: 0.12 },
  { height: 0.85, delay: 0.22 },
  { height: 0.4, delay: 0.08 },
  { height: 0.6, delay: 0.18 },
  { height: 0.75, delay: 0.28 },
];

function InlineWaveform() {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2.5px',
        overflow: 'hidden',
        borderRadius: '50px',
        zIndex: 0,
        px: 2,
        '@keyframes soundwave': {
          '0%, 100%': {
            transform: 'scaleY(0.3)',
          },
          '50%': {
            transform: 'scaleY(1)',
          },
        },
      }}
    >
      {waveformBars.map((bar, i) => (
        <Box
          key={i}
          sx={{
            width: '3px',
            height: `${bar.height * 100}%`,
            maxHeight: '70%',
            minHeight: '15%',
            background: 'linear-gradient(180deg, rgba(181, 104, 60, 0.5) 0%, rgba(181, 104, 60, 0.2) 100%)',
            borderRadius: '3px',
            animation: `soundwave ${0.6 + bar.delay}s ease-in-out ${bar.delay}s infinite`,
          }}
        />
      ))}
    </Box>
  );
}

export default function Hero() {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [containerWidth, setContainerWidth] = React.useState<number | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const nextIndex = (currentIndex + 1) % rotatingPhrases.length;
  
  // Refs to measure phrase widths
  const measureRef = React.useRef<HTMLSpanElement>(null);
  const containerRef = React.useRef<HTMLSpanElement>(null);

  // Measure initial width on mount and when index changes
  React.useEffect(() => {
    if (measureRef.current && !isAnimating) {
      setContainerWidth(measureRef.current.offsetWidth);
    }
  }, [currentIndex, isAnimating]);

  // Rotate through phrases (skipped when the user prefers reduced motion)
  React.useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const interval = setInterval(() => {
      // First, measure the next phrase width
      if (measureRef.current) {
        // Get the computed font size of the measure element
        const computedStyle = window.getComputedStyle(measureRef.current);
        const fontSize = computedStyle.fontSize;
        
        // Create a temporary span to measure next width
        const tempSpan = document.createElement('span');
        tempSpan.style.cssText = `
          font-family: "Instrument Serif", serif;
          font-style: italic;
          white-space: nowrap;
          visibility: hidden;
          position: absolute;
          font-size: ${fontSize};
        `;
        tempSpan.textContent = rotatingPhrases[(currentIndex + 1) % rotatingPhrases.length];
        measureRef.current.parentElement?.appendChild(tempSpan);
        const nextWidth = tempSpan.offsetWidth;
        tempSpan.remove();
        
        // Start animation - set target width
        setIsAnimating(true);
        setContainerWidth(nextWidth);
        
        // After animation completes, update index
        setTimeout(() => {
          setCurrentIndex((prev) => (prev + 1) % rotatingPhrases.length);
          setIsAnimating(false);
        }, ANIMATION_DURATION);
      }
    }, DISPLAY_DURATION);

    return () => clearInterval(interval);
  }, [currentIndex]);

  const handleBookingClick = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 900;
    if (isMobile) {
      window.dispatchEvent(new CustomEvent('openCalendly'));
    } else {
      scrollToSection('booking');
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('openCalendly'));
      }, 500);
    }
  };

  const handleListenExample = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/website_demo_pi.mp3');
      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false);
      });
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // Cleanup audio on unmount
  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 64; // Approximate navbar height
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - navbarHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Box
      id="hero"
      sx={{
        width: '100%',
        minHeight: { xs: '50vh', sm: '45vh' },
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        pt: { xs: 14, sm: 18 },
        pb: { xs: 4, sm: 5 },
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: { xs: 6, md: 8 },
        }}
      >
        <Stack
          spacing={3}
          useFlexGap
          sx={{
            alignItems: { xs: 'center', md: 'flex-start' },
            width: { xs: '100%', md: '54%' },
            flexShrink: 0,
          }}
        >
          {/* Main Headline */}
          <Typography
            variant="h1"
            component="h1"
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              fontSize: { xs: 'clamp(2.75rem, 9vw, 3.75rem)', md: 'clamp(2.75rem, 4.5vw, 4rem)' },
              lineHeight: 1.08,
              fontWeight: 500,
              letterSpacing: '-0.035em',
              color: '#3E301E',
            }}
          >
            {/* Static text for screen readers; the animated words are decorative */}
            <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
              Never miss a client.
            </Box>
            <Box component="span" aria-hidden="true">Never miss{' '}</Box>
            {/* Animated phrase container - uses baseline alignment */}
            <Box
              component="span"
              aria-hidden="true"
              ref={containerRef}
              sx={{
                display: 'inline-flex',
                position: 'relative',
                overflow: 'hidden',
                verticalAlign: 'baseline',
                width: containerWidth ? `${containerWidth + 6}px` : 'auto',
                transition: `width ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
              }}
            >
              {/* Invisible placeholder to maintain baseline and height */}
              <Box
                component="span"
                sx={{
                  ...phraseStyles,
                  visibility: 'hidden',
                  position: 'relative',
                }}
                aria-hidden="true"
              >
                {rotatingPhrases[currentIndex]}
              </Box>
              
              {/* Hidden measure element for width calculation */}
              <Box
                component="span"
                ref={measureRef}
                sx={{
                  ...phraseStyles,
                  visibility: 'hidden',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                }}
                aria-hidden="true"
              >
                {rotatingPhrases[currentIndex]}
              </Box>
              
              {/* Current word - slides out */}
              <Box
                component="span"
                sx={{
                  ...phraseStyles,
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: isAnimating ? 'translateY(-110%)' : 'translateY(0)',
                  transition: isAnimating ? `transform ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)` : 'none',
                }}
              >
                {rotatingPhrases[currentIndex]}
              </Box>
              
              {/* Next word - slides in */}
              <Box
                component="span"
                sx={{
                  ...phraseStyles,
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: isAnimating ? 'translateY(0)' : 'translateY(110%)',
                  transition: isAnimating ? `transform ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)` : 'none',
                }}
              >
                {rotatingPhrases[nextIndex]}
              </Box>
            </Box>
          </Typography>

          {/* Subheadline */}
          <Typography
            variant="body1"
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              color: '#6B5F4D',
              maxWidth: '540px',
              fontSize: { xs: '1.1rem', md: '1.2rem' },
              lineHeight: 1.6,
              textWrap: 'pretty',
            }}
          >
            Every call, text, chat, and form answered in seconds, screened against your criteria, and booked on your calendar. One fully managed AI intake platform, built for law firms.
          </Typography>

          {/* CTA Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mt: 2, width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleBookingClick}
              sx={ctaPrimary}
            >
              Book a demo
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={isPlaying ? <PauseIcon sx={{ zIndex: 1 }} /> : <PlayArrowIcon sx={{ zIndex: 1 }} />}
              onClick={handleListenExample}
              sx={{
                ...ctaSecondary,
                position: 'relative',
                overflow: 'hidden',
                '& .MuiButton-startIcon': { zIndex: 1, color: brand.copper },
              }}
            >
              {isPlaying && <InlineWaveform />}
              <Box component="span" sx={{ zIndex: 1, position: 'relative' }}>
                {isPlaying ? 'Pause' : 'Hear a sample call'}
              </Box>
            </Button>
          </Stack>

          {/* YC Backed Text */}
          <Box
            component="a"
            href="https://www.ycombinator.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              mt: 2,
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease',
              '&:hover': {
                opacity: 0.7,
              },
            }}
          >
            <Box
              component="img"
              src="/yc-logo.svg"
              alt="Y Combinator"
              sx={{
                width: 16,
                height: 16,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: '#6B5F4D',
                fontWeight: 500,
                fontSize: '0.85rem',
              }}
            >
              Backed by Y Combinator
            </Typography>
          </Box>
        </Stack>

        {/* Live qualification card — the product doing its job while you read */}
        <Box
          aria-hidden="true"
          sx={{
            display: { xs: 'none', md: 'block' },
            flexGrow: 1,
            minWidth: 0,
            height: 400,
            borderRadius: 4,
            backgroundColor: brand.ivory,
            border: `1px solid ${brand.sand}`,
            overflow: 'hidden',
          }}
        >
          <QualificationAnimation />
        </Box>
      </Container>
    </Box>
  );
}
